import { ReactNode, createContext, useContext, useMemo } from 'react'

import { useListDoctorsApiDoctorsGet } from '../shared/api/generated/doctors/doctors'

export interface DoctorSummary {
  id: number
  name: string
  notion_name?: string | null
  active: boolean
  // Proposal + contract config (optional — loaded on detail page).
  crm?: string | null
  bio?: string | null
  pricing_model?: string | null
  included_items?: string | null
  default_deposit_cents?: number | null
  deposit_note?: string | null
  validity_note?: string | null
  [extra: string]: unknown
}

interface DoctorsState {
  doctors: DoctorSummary[]
  loadDoctors: () => Promise<unknown>
}

const DoctorsContext = createContext<DoctorsState | null>(null)

/**
 * Thin wrapper around the React Query doctors list. Other features invalidate
 * the same query key after mutations (create/update), so the context updates
 * automatically without manual refetch.
 *
 * Kept for migration compatibility — callers will move to
 * `useListDoctorsApiDoctorsGet` directly and the provider retires in 10.22.
 */
export function DoctorsProvider({ children }: { children: ReactNode }) {
  const query = useListDoctorsApiDoctorsGet<DoctorSummary[]>()
  const doctors = query.data ?? []
  const value = useMemo<DoctorsState>(
    () => ({
      doctors,
      loadDoctors: () => query.refetch(),
    }),
    [doctors, query],
  )
  return (
    <DoctorsContext.Provider value={value}>{children}</DoctorsContext.Provider>
  )
}

export function useDoctors(): DoctorsState {
  const ctx = useContext(DoctorsContext)
  if (!ctx) throw new Error('useDoctors must be used within DoctorsProvider')
  return ctx
}
